import type { AICaptureStatsResponse, AIEventFilters, AIEventLabelFacetResponse, AIEventListResponse, AIFaceCaptureFilters, AIFaceCaptureListResponse, AIReviewStatus, CameraAIProfile } from '@/types/ai'
import type { AdminSession, APIErrorBody, CameraGroupListResponse, CameraGroupSettings, CameraListResponse, DeviceCredential, Provisioning } from '@/types/monitor'
import type { RecordingFilters, RecordingListResponse, RecordingPlaybackControlResponse, RecordingPlaybackExtension, RecordingPlaybackResponse, RecordingStorageStatus } from '@/types/recording'
import type { MonitorWall, MonitorWallListResponse, MonitorWallPayload, PublicWall } from '@/types/wall'

const apiBaseURL = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/$/, '')
const tokenKey = 'qdd-monitor-session'
let loginRedirect = false

export class APIError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export function loadSession(): AdminSession | null {
  try {
    const persistentValue = localStorage.getItem(tokenKey)
    if (persistentValue) return JSON.parse(persistentValue) as AdminSession

    // Migrate an active session saved by older builds without forcing the
    // administrator to sign in again when this version is first loaded.
    const legacyValue = sessionStorage.getItem(tokenKey)
    if (!legacyValue) return null
    const session = JSON.parse(legacyValue) as AdminSession
    localStorage.setItem(tokenKey, legacyValue)
    sessionStorage.removeItem(tokenKey)
    return session
  } catch {
    localStorage.removeItem(tokenKey)
    sessionStorage.removeItem(tokenKey)
    return null
  }
}

export function saveSession(session: AdminSession | null): void {
  if (session) localStorage.setItem(tokenKey, JSON.stringify(session))
  else localStorage.removeItem(tokenKey)
  sessionStorage.removeItem(tokenKey)
}

function redirectToLogin(): void {
  if (!loginRedirect) {
    loginRedirect = true
    window.location.replace('/login')
  }
}

function requestURL(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  return `${apiBaseURL}${path}`
}

// WHEP exchanges raw application/sdp rather than JSON, but it must use the
// same bearer/session-expiry behavior as every other admin API request.
export async function signalingRequest(path: string, init: RequestInit, authenticated: boolean): Promise<Response> {
  const headers = new Headers(init.headers)
  const session = loadSession()
  if (authenticated && session?.token) headers.set('Authorization', session.token)
  const response = await fetch(requestURL(path), { ...init, headers, cache: 'no-store' })
  if (response.ok) return response

  let message = ''
  try {
    const contentType = response.headers.get('Content-Type') || ''
    if (contentType.includes('application/json')) {
      message = ((await response.json()) as APIErrorBody).message || ''
    } else {
      message = (await response.text()).trim()
    }
  } catch {
    // Use the status fallback below when a proxy returned an unreadable body.
  }
  if (response.status === 401 && authenticated) {
    saveSession(null)
    redirectToLogin()
  }
  throw new APIError(message || `视频连接失败（${response.status}）`, response.status)
}

async function request<T>(path: string, init: RequestInit = {}, authenticated = true, redirectOnUnauthorized = true): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body) headers.set('Content-Type', 'application/json')
  const session = loadSession()
  if (authenticated && session?.token) headers.set('Authorization', session.token)
  // A previous deployment may have cached an HTML fallback at an API URL.
  // Bypass that entry: the new backend's no-store header cannot repair a cache hit.
  const response = await fetch(requestURL(path), { ...init, headers, cache: 'no-store' })
  if (!response.ok) {
    let body: APIErrorBody = {}
    try {
      body = (await response.json()) as APIErrorBody
    } catch {
      // The status-specific fallback below remains more useful than a parse error.
    }
    if (response.status === 401) {
      saveSession(null)
      if (authenticated && redirectOnUnauthorized) redirectToLogin()
    }
    throw new APIError(body.message || `请求失败（${response.status}）`, response.status)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** AbortSignal.timeout without its browser floor: displays run old browsers. */
function deadline(milliseconds: number): AbortSignal {
  const controller = new AbortController()
  window.setTimeout(() => controller.abort(), milliseconds)
  return controller.signal
}

function aiEventQuery(filters: AIEventFilters, pagination: boolean): URLSearchParams {
  const query = new URLSearchParams()
  if (filters.deviceIds?.length) query.set('deviceIds', filters.deviceIds.join(','))
  else if (filters.deviceId) query.set('deviceId', filters.deviceId)
  if (filters.channelId) query.set('channelId', filters.channelId)
  if (filters.type) query.set('type', filters.type)
  if (filters.status) query.set('status', filters.status)
  if (filters.aiReviewed !== undefined) query.set('aiReviewed', String(filters.aiReviewed))
  if (filters.labels?.length) {
    query.set('labels', JSON.stringify(filters.labels.map(({ dimension, value }) => ({ dimension, value }))))
  }
  if (filters.from) query.set('from', filters.from)
  if (filters.to) query.set('to', filters.to)
  if (pagination && filters.limit) query.set('limit', String(filters.limit))
  if (pagination && filters.before) query.set('before', filters.before)
  if (pagination && filters.beforeId !== undefined) query.set('beforeId', String(filters.beforeId))
  return query
}

export const monitorAPI = {
  login: (username: string, password: string) =>
    request<AdminSession>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }, false),
  // Route guards must finish verifying and clear the in-memory auth store.
  // They own navigation; replacing the document here can strand initial routing.
  session: () => request<{ username: string }>('/api/admin/auth/session', {}, true, false),
  logout: () => request<void>('/api/admin/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/admin/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  provisioning: () => request<Provisioning>('/api/admin/provisioning'),
  cameraGroups: () => request<CameraGroupListResponse>('/api/admin/camera-groups'),
  updateCameraGroup: (name: string, mediaHost: string) =>
    request<CameraGroupSettings>('/api/admin/camera-groups', {
      method: 'PUT',
      body: JSON.stringify({ name, mediaHost }),
    }),
  cameras: (keyword = '', status = '') => {
    const query = new URLSearchParams()
    if (keyword) query.set('keyword', keyword)
    if (status) query.set('status', status)
    const suffix = query.size ? `?${query.toString()}` : ''
    return request<CameraListResponse>(`/api/admin/cameras${suffix}`)
  },
  createCamera: (deviceId: string, name: string, groupName: string, connectionCommand: string) =>
    request<DeviceCredential>('/api/admin/cameras', {
      method: 'POST',
      body: JSON.stringify({ deviceId, name, groupName, connectionCommand }),
    }),
  cameraCredential: (deviceId: string) =>
    request<DeviceCredential>(`/api/admin/cameras/${encodeURIComponent(deviceId)}/credential`),
  rotateCameraCredential: (deviceId: string) =>
    request<DeviceCredential>(`/api/admin/cameras/${encodeURIComponent(deviceId)}/credential/rotate`, { method: 'POST' }),
  updateCamera: (deviceId: string, name: string, groupName: string, connectionCommand: string) =>
    request<void>(`/api/admin/cameras/${encodeURIComponent(deviceId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name, groupName, connectionCommand }),
    }),
  deleteCamera: (deviceId: string) =>
    request<void>(`/api/admin/cameras/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),
  monitorWalls: () => request<MonitorWallListResponse>('/api/admin/monitor-walls'),
  createMonitorWall: (payload: MonitorWallPayload) =>
    request<MonitorWall>('/api/admin/monitor-walls', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateMonitorWall: (id: string, payload: MonitorWallPayload) =>
    request<MonitorWall>(`/api/admin/monitor-walls/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteMonitorWall: (id: string) =>
    request<void>(`/api/admin/monitor-walls/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Deliberately unauthenticated: the shared wall page runs on a display that
  // was never logged in and has no session to renew. The deadline matters more
  // than elsewhere too — an unattended page polls this forever, and a request
  // left hanging on a half-open connection would never come back on its own.
  publicWall: (id: string) =>
    request<PublicWall>(`/api/public/walls/${encodeURIComponent(id)}`, { signal: deadline(10_000) }, false),
  recordings: (filters: RecordingFilters = {}) => {
    const query = new URLSearchParams()
    if (filters.deviceId) query.set('deviceId', filters.deviceId)
    if (filters.streamId) query.set('streamId', filters.streamId)
    if (filters.status) query.set('status', filters.status)
    if (filters.from) query.set('from', filters.from)
    if (filters.to) query.set('to', filters.to)
    if (filters.limit) query.set('limit', String(filters.limit))
    if (filters.before) query.set('before', filters.before)
    if (filters.beforeId) query.set('beforeId', filters.beforeId)
    const suffix = query.size ? `?${query.toString()}` : ''
    return request<RecordingListResponse>(`/api/admin/recordings${suffix}`)
  },
  recordingStorage: () => request<RecordingStorageStatus>('/api/admin/recording-storage'),
  startRecordingPlayback: (id: string, seekMs: number, speed: number, scope: 'session' | 'clip' = 'session') =>
    request<RecordingPlaybackResponse>(`/api/admin/recordings/${encodeURIComponent(id)}/playback`, {
      method: 'POST',
      body: JSON.stringify({ seekMs, speed, scope }),
    }),
  controlRecordingPlayback: (sessionId: string, control: { seekMs?: number, speed?: number }) =>
    request<RecordingPlaybackControlResponse>(`/api/admin/recording-playback/${encodeURIComponent(sessionId)}/control`, {
      method: 'POST',
      body: JSON.stringify(control),
    }),
  extendRecordingPlayback: (sessionId: string, extension: { afterRecordingId: string, offsetMs: number }) =>
    request<RecordingPlaybackExtension>(`/api/admin/recording-playback/${encodeURIComponent(sessionId)}/extend`, {
      method: 'POST',
      body: JSON.stringify(extension),
    }),
  aiCaptures: (filters: AIFaceCaptureFilters = {}) => {
    const query = new URLSearchParams()
    if (filters.deviceIds?.length) query.set('deviceIds', filters.deviceIds.join(','))
    else if (filters.deviceId) query.set('deviceId', filters.deviceId)
    if (filters.channelId) query.set('channelId', filters.channelId)
    if (filters.minDetScore !== undefined) query.set('minDetScore', String(filters.minDetScore))
    if (filters.minFacePixels !== undefined) query.set('minFacePixels', String(filters.minFacePixels))
    if (filters.minFrontality !== undefined) query.set('minFrontality', String(filters.minFrontality))
    if (filters.minEmbeddingNorm !== undefined) query.set('minEmbeddingNorm', String(filters.minEmbeddingNorm))
    if (filters.from) query.set('from', filters.from)
    if (filters.to) query.set('to', filters.to)
    if (filters.limit) query.set('limit', String(filters.limit))
    if (filters.before) query.set('before', filters.before)
    if (filters.beforeId !== undefined) query.set('beforeId', String(filters.beforeId))
    const suffix = query.size ? `?${query.toString()}` : ''
    return request<AIFaceCaptureListResponse>(`/api/admin/ai/captures${suffix}`)
  },
  aiEvents: (filters: AIEventFilters = {}) => {
    const query = aiEventQuery(filters, true)
    const suffix = query.size ? `?${query.toString()}` : ''
    return request<AIEventListResponse>(`/api/admin/ai/events${suffix}`)
  },
  aiEventLabelFacets: (filters: AIEventFilters = {}) => {
    const query = aiEventQuery(filters, false)
    const suffix = query.size ? `?${query.toString()}` : ''
    return request<AIEventLabelFacetResponse>(`/api/admin/ai/event-label-facets${suffix}`)
  },
  reviewAIEvent: (id: number, status: AIReviewStatus, note = '') =>
    request<void>(`/api/admin/ai/events/${encodeURIComponent(String(id))}/review`, {
      method: 'POST',
      body: JSON.stringify({ status, note }),
    }),
  aiCaptureStats: (since?: string) => {
    const suffix = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<AICaptureStatsResponse>(`/api/admin/ai/capture-stats${suffix}`)
  },
  aiProfile: (deviceId: string, channelId: string) =>
    request<CameraAIProfile>(
      `/api/admin/ai/cameras/${encodeURIComponent(deviceId)}/${encodeURIComponent(channelId)}/profile`,
    ),
  saveAIProfile: (deviceId: string, channelId: string, profile: CameraAIProfile) =>
    request<CameraAIProfile>(
      `/api/admin/ai/cameras/${encodeURIComponent(deviceId)}/${encodeURIComponent(channelId)}/profile`,
      { method: 'PUT', body: JSON.stringify(profile) },
    ),
}
